import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Card } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getImagePath } from '../utils/imageHelper';
import WorkspacePicker, { isWorkspaceSelectable } from '../components/auth/WorkspacePicker';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [choosingKey, setChoosingKey] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [step, setStep] = useState('credentials');
  const [workspaces, setWorkspaces] = useState([]);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const navigate = useNavigate();
  const { login, chooseWorkspace } = useAuth();
  const { isDarkMode } = useTheme();
  const [form] = Form.useForm();

  useEffect(() => {
    const imagePath = getImagePath('/images/bg_image.webp');
    const img = new Image();
    img.onload = () => setBackgroundImage(imagePath);
    img.onerror = () => setBackgroundImage('');
    img.src = imagePath;
  }, []);

  const goToDashboard = () => {
    message.success('ورود با موفقیت انجام شد');
    setTimeout(() => navigate('/dashboard'), 400);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const result = await login(values);
      if (result.success) {
        goToDashboard();
        return;
      }
      if (result.requiresWorkspaceSelection) {
        const selectable = (result.workspaces || []).filter(isWorkspaceSelectable);
        if (selectable.length === 1) {
          const singleWs = selectable[0];
          setChoosingKey(`${singleWs.user_id}-${singleWs.company_id}`);
          const wsRes = await chooseWorkspace({
            email: values.email,
            password: values.password,
            user_id: singleWs.user_id,
            company_id: singleWs.company_id,
          });
          if (wsRes.success) {
            goToDashboard();
            return;
          }
          message.error(wsRes.error || 'خطا در ورود به پنل');
          return;
        }
        if (selectable.length === 0) {
          message.error('حساب کاربری شما در هیچ‌یک از شرکت‌ها فعال نیست');
          return;
        }
        setCredentials({ email: values.email, password: values.password });
        setWorkspaces(result.workspaces || []);
        setStep('workspace');
        return;
      }
      message.error(result.error || 'خطا در ورود به سیستم');
    } catch (error) {
      console.error('Login error:', error);
      message.error(error?.message || 'خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceSelect = async (ws) => {
    setChoosingKey(`${ws.user_id}-${ws.company_id}`);
    try {
      const result = await chooseWorkspace({
        email: credentials.email,
        password: credentials.password,
        user_id: ws.user_id,
        company_id: ws.company_id,
      });
      if (result.success) {
        goToDashboard();
        return;
      }
      if (result.requiresWorkspaceSelection) {
        setWorkspaces(result.workspaces || []);
        if (result.message) {
          message.info(result.message);
        }
        return;
      }
      message.error(result.error || 'خطا در ورود به پنل');
    } catch (error) {
      message.error(error?.message || 'خطا در ارتباط با سرور');
    } finally {
      setChoosingKey(null);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setWorkspaces([]);
    setChoosingKey(null);
  };

  const isWorkspaceStep = step === 'workspace';

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 relative ${
        !backgroundImage && isDarkMode ? 'panel-dark-bg' : ''
      } ${!backgroundImage && !isDarkMode ? 'bg-[#f0f0f0]' : ''}`}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: backgroundImage ? 'transparent' : undefined,
      }}
    >
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />

      <div
        className={`relative z-10 w-full transition-all duration-300 ${
          isWorkspaceStep ? 'max-w-3xl' : 'max-w-md'
        }`}
      >
        <Card
          className={`rounded-2xl shadow-2xl overflow-hidden ${
            isDarkMode
              ? 'panel-dark-chrome backdrop-blur-md border border-[rgba(13,136,126,0.22)]'
              : 'bg-white/95 backdrop-blur-md border-0'
          }`}
          styles={{ body: { padding: isWorkspaceStep ? '2rem 1.75rem' : '3rem' } }}
        >
          {isWorkspaceStep ? (
            <WorkspacePicker
              workspaces={workspaces}
              loadingKey={choosingKey}
              isDarkMode={isDarkMode}
              onSelect={handleWorkspaceSelect}
              onBack={handleBackToCredentials}
            />
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="mb-6">
                  <div className="flex justify-center mb-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden shadow-lg ring-4 bg-brand-primary flex items-center justify-center ring-[rgba(13,136,126,0.35)] dark:ring-[rgba(13,136,126,0.35)]">
                      <span className="text-white text-2xl font-bold" style={{ fontFamily: 'CustomFont' }}>
                        ERP
                      </span>
                    </div>
                  </div>
                </div>

                <Title
                  level={2}
                  className={`!mb-2 !font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  خوش آمدید
                </Title>
                <Text
                  className={`text-base ${isDarkMode ? 'text-white/65' : 'text-gray-600'}`}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  لطفاً برای ورود به پنل مدیریت، اطلاعات خود را وارد کنید
                </Text>
              </div>

              <Form
                form={form}
                name="login"
                onFinish={onFinish}
                layout="vertical"
                size="middle"
                className="mt-8"
                initialValues={{ email: credentials.email }}
              >
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'لطفاً ایمیل را وارد کنید' },
                    { type: 'email', message: 'لطفاً یک ایمیل معتبر وارد کنید' },
                  ]}
                  className="mb-6"
                  style={{ marginBottom: '24px' }}
                >
                  <Input
                    prefix={<UserOutlined className={isDarkMode ? 'text-white/50' : 'text-gray-500'} />}
                    placeholder="ایمیل"
                    className={`h-12 rounded-lg border-2 transition-all ${
                      isDarkMode
                        ? 'bg-brand-dark border-[rgba(13,136,126,0.22)] text-white focus:border-brand-primary'
                        : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-brand-primary'
                    }`}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'لطفاً رمز عبور را وارد کنید' }]}
                  className="mb-6"
                >
                  <Input.Password
                    prefix={<LockOutlined className={isDarkMode ? 'text-white/50' : 'text-gray-500'} />}
                    placeholder="رمز عبور"
                    className={`h-12 rounded-lg border-2 transition-all ${
                      isDarkMode
                        ? 'bg-brand-dark border-[rgba(13,136,126,0.22)] text-white focus:border-brand-primary'
                        : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-brand-primary'
                    }`}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>

                <Form.Item className="mb-4">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    className="h-12 rounded-lg text-base font-semibold shadow-lg hover:shadow-xl transition-all bg-brand-primary hover:bg-brand-primary-hover border-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {loading ? 'در حال ورود...' : 'ورود به پنل'}
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </Card>
      </div>

      <style>{`
        .ant-form-item-has-error {
          margin-bottom: 28px !important;
        }
        .ant-form-item-explain-error {
          margin-top: 6px;
          margin-bottom: 12px;
          display: block;
        }
      `}</style>
    </div>
  );
};

export default Login;
